import type { BrandProfile } from "./business";
import type { SiteDocument, Section } from "./types";
import { legalNoticeKind } from "./legal-notices";

/** Keep complete facts in Brand/detail pages; landing-page copy is a teaser. */
export function shortCopy(value: string, max = 200): string {
  const text = value.replace(/\s+/g," ").trim();
  if (text.length <= max) return text;
  const slice = text.slice(0,max - 1);
  const sentences = [...slice.matchAll(/[.!?](?=\s|$)/g)];
  const last = sentences.at(-1)?.index;
  if (last !== undefined && last > max * .35) return slice.slice(0,last + 1);
  const end = slice.lastIndexOf(" ");
  return slice.slice(0,end > max * .5 ? end : slice.length).trimEnd() + "…";
}

export function deduplicateStaffPhotos(current: SiteDocument, brand: BrandProfile, generatedSources: Record<string,string> = {}, pageIds?: Set<string>): SiteDocument {
  const sources = new Map(brand.media.filter(photo=>photo.useInWebsite && photo.category === "team").map(photo=>[photo.url,photo.id]));
  Object.entries(generatedSources).forEach(([url,id])=>sources.set(url,id));
  // A previously saved illustration may not have its source alias in this edit.
  // Still recognize identical portraits reused outside their team widget.
  for (const page of current.pages) for (const section of page.sections) {
    if (section.type === "about" && section.team && section.image && !sources.has(section.image)) sources.set(section.image, `portrait:${section.image}`);
    if (section.type === "gallery" && section.showCaptions) for (const image of section.images) {
      const photo = brand.media.find(photo => photo.useInWebsite && photo.category === "team" && photo.id === image.id);
      if (photo && !sources.has(image.url)) sources.set(image.url, photo.id);
    }
  }
  if (!sources.size) return current;
  const site = structuredClone(current);
  for(const page of site.pages) {
    if(legalNoticeKind(page.slug) || (pageIds && !pageIds.has(page.id))) continue;
    const seen = new Set<string>();
    // Reserve a staff photo for its actual team story/portrait, not a generic
    // business teaser or hero. The page's visual section order is unchanged.
    const priority = (section: Section) => section.type === "about" && section.team ? 0 : section.type === "gallery" && section.showCaptions ? 1 : 2;
    for(const section of [...page.sections].filter(section=>section.visible).sort((a,b)=>priority(a)-priority(b))) {
      const keep = (url: string) => {const source=sources.get(url);if(!source)return true;if(seen.has(source))return false;seen.add(source);return true;};
      if(section.type === "hero" || section.type === "about") {if(section.image && !keep(section.image)) delete section.image;}
      else if(section.type === "gallery") {const before=section.images.length;section.images=section.images.filter(image=>keep(image.url));if(before && !section.images.length)section.visible=false;}
    }
  }
  return site;
}

export function initialComposition(current: SiteDocument, brand: BrandProfile, generatedSources: Record<string,string> = {}): SiteDocument {
  const site = deduplicateStaffPhotos(current,brand,generatedSources);
  const next = site === current ? structuredClone(site) : site;
  for(const page of next.pages.filter(page => !legalNoticeKind(page.slug))) for(const section of page.sections) {
    if(section.type === "hero") {
      section.subtitle = shortCopy(section.subtitle, page.slug ? 180 : 200);
      section.eyebrow = shortCopy(section.eyebrow,70);
      if(!page.slug) {
        section.presentation ??= brand.visualStyle === "glass" ? "glass" : brand.visualStyle === "minimal" ? "cover" : "split";
        if(section.presentation !== "cover") section.design = { imageShape:"arch",imagePosition:"center",...section.design };
      }
    }
    if(!page.slug) {
      if(section.type === "about") section.body=shortCopy(section.body,280);
      else if(section.type === "services" || section.type === "contact" || section.type === "faq") section.intro=shortCopy(section.intro,180);
      else if(section.type === "cta") section.body=shortCopy(section.body,180);
    }
  }
  return next;
}
